import { memo } from 'react';
import styled, { css } from 'styled-components';
import { NavLink, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAdd,        // if this errors, replace with faPlus
  faArrowLeftLong,
  faBank,
  faFileExcel,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';

/* THEME COLORS */
const headerBg = '#eef2ff';
const headerBorder = '#eef2ff';
const blue = '#3b82f6';
const blueHover = '#2563eb';
const green = '#16a34a';
const greenHover = '#0f6a32';
const purple = '#673ab7';
const purpleHover = '#3f1e78';

const StyledPageHeaderContainer = styled.section`
  background-color: ${headerBg};
  border-bottom: 1px solid ${headerBorder};
  padding: 15px 25px;
  margin: 0;
  display: flex;
  gap: 20px;
  align-items: center;
  backdrop-filter: blur(6px);

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    row-gap: 10px;
  }

  h1 {
    font-size: 26px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
  }
`;

const StyledPageHeadingContainer = styled.section`
  display: flex;
  gap: 12px;
  flex: 1;
  align-items: center;
`;

const StyledPageHeadingActionsContainer = styled.section`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 600px) {
    width: 100%;
    flex-wrap: wrap;
    row-gap: 10px;
  }
`;

const StyledBackButtonContainer = styled.button`
  background: transparent;
  border: 0;
  padding: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;

  svg {
    font-size: 20px;
    color: #475569;
  }

  &:hover svg {
    color: #1e293b;
  }
`;

const StyledHeadingContainer = styled.section`
  flex: 1;
  display: flex;
  align-items: center;
`;

/* shared button styles using css helper */
const buttonStyles = css`
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  padding: 10px 20px;
  border-radius: 8px;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.15s ease;
  text-decoration: none;

  @media (max-width: 600px) {
    padding: 10px 14px;
  }

  svg {
    display: inline-block;
  }
`;

/* NavLink styled as button for Add (uses anchor) */
const AddBtn = styled(NavLink)`
  ${buttonStyles}
  background-color: ${green};
  &:hover {
    background-color: ${greenHover};
  }
`;

/* Normal button variants */
const LoadBtn = styled.button`
  ${buttonStyles}
  background-color: ${green};
  &:hover {
    background-color: ${greenHover};
  }
`;

const ExportBtn = styled.button`
  ${buttonStyles}
  background-color: ${purple};
  &:hover {
    background-color: ${purpleHover};
  }
`;

const ReloadBtn = styled.button`
  ${buttonStyles}
  background-color: ${blue};
  &:hover {
    background-color: ${blueHover};
  }
`;

/* PageHeader component */
const PageHeader = memo(
  ({
    title,
    showBackButton = false,
    showAddButton = false,
    addLink = 'add',
    addTitle = 'Add',
    showReloadData = false,
    reloadData = () => {},
    showExportButton = false,
    onClickExport = () => {},
    showLoadButton = false,
    onClickLoad = () => {},
  }) => {
    const navigate = useNavigate();
    const goBack = () => navigate(-1);

    return (
      <StyledPageHeaderContainer>
        <StyledPageHeadingContainer>
          {showBackButton && (
            <StyledBackButtonContainer onClick={goBack} aria-label="Go back">
              <FontAwesomeIcon icon={faArrowLeftLong} />
            </StyledBackButtonContainer>
          )}

          <StyledHeadingContainer>
            <h1>{title}</h1>
          </StyledHeadingContainer>
        </StyledPageHeadingContainer>

        <StyledPageHeadingActionsContainer>
          {showAddButton && (
            <AddBtn to={addLink} title={addTitle}>
              <FontAwesomeIcon icon={faAdd} /> {addTitle}
            </AddBtn>
          )}

          {showLoadButton && (
            <LoadBtn onClick={onClickLoad} type="button" title="Load">
              <FontAwesomeIcon icon={faBank} /> Load
            </LoadBtn>
          )}

          {showExportButton && (
            <ExportBtn onClick={onClickExport} type="button" title="Export">
              <FontAwesomeIcon icon={faFileExcel} /> Export
            </ExportBtn>
          )}

          {showReloadData && (
            <ReloadBtn onClick={reloadData} type="button" title="Refresh">
              <FontAwesomeIcon icon={faRefresh} /> Refresh
            </ReloadBtn>
          )}
        </StyledPageHeadingActionsContainer>
      </StyledPageHeaderContainer>
    );
  }
);

export default PageHeader;
